import React, { useEffect, useState, useRef } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePieceStore } from '../stores/usePieceStore';
import { useRoughBodyStore } from '../stores/useRoughBodyStore';
import { ChevronLeft, Zap, Keyboard, Camera, Search, Loader2 } from 'lucide-react';
import { clsx } from 'clsx';

type ScanMode = 'camera' | 'manual';

export const Scanner: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { findPieceByQrCode } = usePieceStore();
    const { findRoughBodyByQrCode } = useRoughBodyStore();
    const [error, setError] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [mode, setMode] = useState<ScanMode>('camera');
    const [manualCode, setManualCode] = useState('');

    const scannerRef = useRef<Html5Qrcode | null>(null);
    const isMounted = useRef(true);
    const isProcessingRef = useRef(false);
    const hasInitialized = useRef(false);

    // ── Camera Scanner ──
    useEffect(() => {
        if (mode !== 'camera') return;

        isMounted.current = true;
        if (hasInitialized.current) return;
        hasInitialized.current = true;

        let scanner: Html5Qrcode | null = null;

        const initScanner = async () => {
            await new Promise(r => setTimeout(r, 200));
            if (!isMounted.current) return;

            const element = document.getElementById('reader');
            if (!element) { setError("Scanner view missing"); return; }
            element.innerHTML = "";

            try {
                scanner = new Html5Qrcode("reader", {
                    verbose: false,
                    formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE]
                });
                scannerRef.current = scanner;

                const devices = await Html5Qrcode.getCameras();
                if (!devices || devices.length === 0) throw new Error("No camera found");

                let cameraId = devices[0].id;
                const backCam = devices.find(d => d.label.toLowerCase().includes('back'));
                if (backCam) cameraId = backCam.id;
                else if (devices.length > 1) cameraId = devices[devices.length - 1].id;

                if (!isMounted.current) return;

                const ratio = window.innerWidth / window.innerHeight;

                await scanner.start(
                    cameraId,
                    { fps: 10, qrbox: { width: 250, height: 250 }, aspectRatio: ratio, disableFlip: false },
                    (decodedText) => { if (isMounted.current) handleScan(decodedText); },
                    () => { }
                );
            } catch (err: any) {
                console.error("Scanner Error:", err);
                if (isMounted.current) setError(err.message || "Camera access denied");
            }
        };

        initScanner();

        return () => {
            isMounted.current = false;
            hasInitialized.current = false;
            if (scanner) {
                if (scanner.isScanning) {
                    scanner.stop().then(() => scanner?.clear()).catch(() => { });
                } else {
                    try { scanner.clear(); } catch (e) { }
                }
            }
        };
    }, [mode]);

    // ── Stop camera when switching to manual ──
    const switchMode = async (newMode: ScanMode) => {
        if (newMode === mode) return;

        // Stop camera if switching to manual
        if (mode === 'camera' && scannerRef.current) {
            try {
                if (scannerRef.current.isScanning) {
                    await scannerRef.current.stop();
                }
                scannerRef.current.clear();
            } catch (e) { }
            scannerRef.current = null;
            hasInitialized.current = false;
        }

        setError(null);
        setIsProcessing(false);
        isProcessingRef.current = false;
        setManualCode('');
        setMode(newMode);
    };

    // ── Shared scan handler ──
    const handleScan = async (code: string) => {
        if (isProcessingRef.current) return;
        isProcessingRef.current = true;
        setIsProcessing(true);
        setError(null);

        const expectedSampleId = (location.state as any)?.sampleId;

        try {
            if (code.startsWith('RB-')) {
                // ROUGH BODY FLOW
                const result = await findRoughBodyByQrCode(code);
                if (result && result.id) {
                    navigate(`/rough-body/${code}`);
                    return;
                } else {
                    throw new Error("Rough Body Not Found");
                }
            }

            const result = await findPieceByQrCode(code);
            if (result && result.id) {
                // Validation: Check if piece belongs to expected sample
                if (expectedSampleId && result.sample_request_id !== expectedSampleId) {
                    throw new Error("Piece belongs to a different sample.");
                }

                navigate(`/piece/${result.id}`, { state: { fromScan: true } });
            } else {
                throw new Error("Unknown QR");
            }
        } catch (err: any) {
            setError(err.message || "Invalid Code — not found");
            setTimeout(() => {
                if (isMounted.current) {
                    setError(null);
                    isProcessingRef.current = false;
                    setIsProcessing(false);
                }
            }, 2000);
        }
    };

    const handleManualSubmit = () => {
        const code = manualCode.trim();
        if (!code) return;
        handleScan(code);
    };

    // ═══════════════════════════════════════
    // CAMERA MODE
    // ═══════════════════════════════════════
    if (mode === 'camera') {
        return (
            <div className="fixed inset-0 bg-black z-50 flex flex-col">
                {/* Camera Feed */}
                <div className="absolute inset-0 bg-black">
                    <div id="reader" className="w-full h-full bg-black" />
                </div>

                {/* UI Overlay */}
                <div className="absolute inset-0 z-10 flex flex-col pointer-events-none">
                    {/* Header */}
                    <div className="pt-safe px-5 py-5 flex justify-between items-start pointer-events-auto">
                        <button
                            onClick={() => navigate(-1)}
                            className="w-10 h-10 rounded-xl bg-black/40 backdrop-blur-md flex items-center justify-center text-white border border-white/10 active:scale-95 transition-transform"
                        >
                            <ChevronLeft size={20} />
                        </button>
                        <button
                            onClick={() => switchMode('manual')}
                            className="flex items-center gap-1.5 bg-white/15 backdrop-blur-md text-white px-3.5 py-2 rounded-xl border border-white/10 active:scale-95 transition-transform text-xs font-bold"
                        >
                            <Keyboard size={14} />
                            Manual
                        </button>
                    </div>

                    {/* Scanner Frame */}
                    <div className="flex-1 flex flex-col items-center justify-center pb-20">
                        <div className="relative w-64 h-64">
                            <div className="absolute top-0 left-0 w-10 h-10 border-t-[5px] border-l-[5px] border-white rounded-tl-[20px]" />
                            <div className="absolute top-0 right-0 w-10 h-10 border-t-[5px] border-r-[5px] border-white rounded-tr-[20px]" />
                            <div className="absolute bottom-0 left-0 w-10 h-10 border-b-[5px] border-l-[5px] border-white rounded-bl-[20px]" />
                            <div className="absolute bottom-0 right-0 w-10 h-10 border-b-[5px] border-r-[5px] border-white rounded-br-[20px]" />

                            {!isProcessing && !error && (
                                <div className="absolute top-0 left-0 right-0 h-0.5 bg-blue-500 shadow-[0_0_20px_rgba(59,130,246,1)] animate-scan" />
                            )}

                            {error && (
                                <div className="absolute inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm rounded-[16px]">
                                    <div className="px-4 py-2 bg-red-500/90 text-white font-bold text-sm rounded-lg">{error}</div>
                                </div>
                            )}

                            {isProcessing && !error && (
                                <div className="absolute inset-0 flex items-center justify-center bg-emerald-500/20 backdrop-blur-sm rounded-[16px]">
                                    <div className="w-10 h-10 border-3 border-white border-t-transparent rounded-full animate-spin" />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Bottom Card */}
                    <div className="bg-white/95 backdrop-blur-xl px-6 pt-6 pb-safe rounded-t-3xl shadow-[0_-8px_30px_rgba(0,0,0,0.15)] border-t border-slate-200/50 pointer-events-auto">
                        <div className="text-center pb-6">
                            <h2 className="text-lg font-bold text-slate-800">Scan QR Code</h2>
                            <p className="text-slate-400 text-xs font-medium mt-1">Point camera at QR code to scan automatically</p>
                        </div>
                    </div>
                </div>

                <style>{`
                    #reader video { object-fit: cover !important; width: 100% !important; height: 100% !important; border-radius: 0 !important; }
                    #reader img, #reader > span, #reader > div > span { display: none !important; }
                    @keyframes scan {
                        0% { top: 10%; opacity: 0; }
                        20% { opacity: 1; }
                        80% { opacity: 1; }
                        100% { top: 90%; opacity: 0; }
                    }
                    .animate-scan { animation: scan 2s cubic-bezier(0.4, 0, 0.2, 1) infinite; }
                `}</style>
            </div>
        );
    }

    // ═══════════════════════════════════════
    // MANUAL INPUT MODE
    // ═══════════════════════════════════════
    return (
        <div className="fixed inset-0 bg-stone-50 z-50 flex flex-col">
            {/* Header */}
            <div className="bg-white/80 backdrop-blur-2xl pt-safe px-5 pb-3 border-b border-slate-100/80">
                <div className="flex items-center justify-between py-3">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate(-1)}
                            className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500 active:bg-slate-200 transition-colors"
                        >
                            <ChevronLeft size={18} />
                        </button>
                        <h1 className="text-lg font-bold text-slate-800">Manual Entry</h1>
                    </div>
                    <button
                        onClick={() => switchMode('camera')}
                        className="flex items-center gap-1.5 bg-blue-600 text-white px-3.5 py-2 rounded-xl text-xs font-bold shadow-sm shadow-blue-600/20 active:scale-95 transition-all"
                    >
                        <Camera size={14} />
                        Camera
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 flex flex-col items-center justify-center px-6 animate-page">
                <div className="w-full max-w-sm space-y-6">
                    {/* Icon */}
                    <div className="text-center">
                        <div className="w-16 h-16 rounded-2xl bg-blue-50 mx-auto mb-4 flex items-center justify-center">
                            <Keyboard size={28} className="text-blue-600" />
                        </div>
                        <h2 className="text-xl font-bold text-slate-800 mb-1">Enter QR Code</h2>
                        <p className="text-slate-400 text-xs font-medium">Type or paste the piece QR code below</p>
                    </div>

                    {/* Input */}
                    <div className="space-y-3">
                        <div className="relative">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={18} />
                            <input
                                type="text"
                                autoFocus
                                value={manualCode}
                                onChange={(e) => { setManualCode(e.target.value); setError(null); }}
                                onKeyDown={(e) => e.key === 'Enter' && handleManualSubmit()}
                                placeholder="e.g. QT-24-001-P01"
                                className="w-full bg-white border border-slate-200 rounded-2xl pl-11 pr-4 py-4 text-base text-slate-800 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all font-mono font-medium tracking-wide"
                            />
                        </div>

                        {/* Error */}
                        {error && (
                            <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 text-red-500 text-xs font-bold text-center animate-slide-down">
                                {error}
                            </div>
                        )}

                        {/* Submit */}
                        <button
                            onClick={handleManualSubmit}
                            disabled={!manualCode.trim() || isProcessing}
                            className={clsx(
                                "w-full py-4 rounded-2xl font-bold text-sm transition-all",
                                manualCode.trim()
                                    ? "bg-blue-600 text-white shadow-md shadow-blue-600/20 active:scale-[0.98]"
                                    : "bg-slate-100 text-slate-300 cursor-not-allowed"
                            )}
                        >
                            {isProcessing ? (
                                <Loader2 size={18} className="animate-spin mx-auto" />
                            ) : (
                                'Look Up Piece'
                            )}
                        </button>
                    </div>

                    {/* Tip */}
                    <p className="text-center text-[10px] text-slate-300 font-medium">
                        Enter the exact code printed below the QR
                    </p>
                </div>
            </div>
        </div>
    );
};